import {
  Box,
  Button,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from '@chakra-ui/react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import Container from '../../components/Layout/Container';
import Layout from '../../components/Layout/Layout';
import SearchForm from '../../components/Common/SearchForm';
import SiteLoading from '../../components/Common/SiteLoading';
import TeaserGrid from '../../components/Common/TeaserGrid';
import {
  RATINGS_MAP,
  USER_SELECTED_LOCATION_ON_MAP_TEXT,
} from '../../config/constants';
import {
  HotelActivityPoiConnection,
  Query,
  SearchByCategoryMapQueryQueryVariables,
  SearchByCategoryQueryQuery,
  SearchByCategoryQueryQueryVariables,
  SortOrder,
} from '../../generated/types';
import { SearchByCategoryMapQuery, SearchByCategoryQuery } from '../../queries';
import { ApiDataPageProps } from '../../types/global-types';
import renderTeaser from '../../utils/render-teaser';
import {
  applyMapParamToRoute,
  autocompleteResultToUrl,
  getSearchPath,
} from '../../utils/helper';
import { MapSearchIcon, SortIcon } from '../../config/icons';
import dynamic from 'next/dynamic';
import getGraphqlPageRoute from '../../utils/getGraphqlPageRoute';
import getServerSidePropsData from '../../utils/getServerSidePropsData';
import React, { useState } from 'react';
import { getNumberOfPages, getOffsetForPage } from '../../utils/pagination';
import Pagination from '../../components/Common/Pagination';
import parseFilterQueryString from '../../utils/parseFilterQueryString';
import FilterDrawer from '../../components/Common/FilterDrawer';
import getAppConfig from '../../utils/getAppConfig';
import { ChevronDownIcon } from '@chakra-ui/icons';

const SearchMap = dynamic(() => import('../../components/Common/SearchMap'), {
  ssr: false,
});

const PER_PAGE = 24;

const SearchDiscover: React.FC<
  ApiDataPageProps<SearchByCategoryQueryQuery> & {
    form: {
      query: string;
      radius: number;
      location: {
        lat: number;
        lng: number;
      };
    };
  }
> = ({ data, form = {} }) => {
  const router = useRouter();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const isMap =
    router.query && (router.query.karte === '' || router.query.karte === '1');

  let title =
    data && data.searchByCategory && data.searchByCategory.category
      ? `${data.searchByCategory.category.labelPlural} entdecken`
      : '';

  if (
    data &&
    data.searchByCategory &&
    data.searchByCategory.category &&
    form.query &&
    form.radius
  ) {
    title = `${data.searchByCategory.category.labelPlural} in ${form.query} im Umkreis von ${form.radius} km`;
  }

  // TODO: change title if user selected "Ort auf der karte" ????

  const onGoToMap = () => {
    router.push(applyMapParamToRoute(router.asPath, true));
  };

  const onChangeCenter = (center: any) => {
    if (data.searchByCategory && data.searchByCategory.category) {
      const searchPath = getSearchPath(
        data.searchByCategory.category,
        USER_SELECTED_LOCATION_ON_MAP_TEXT,
        [center.lng.toFixed(6), center.lat.toFixed(6)],
        (form.radius || 30) + ''
      );

      router.push(applyMapParamToRoute(searchPath, isMap));
    }
  };

  const hasData =
    data &&
    data.searchByCategory &&
    data.searchByCategory.result &&
    data.searchByCategory.result.total > 0;

  const currentPage = parseInt(router.query.page as string, 10) || 1;
  const currentOffset = getOffsetForPage(currentPage, PER_PAGE);
  const numberOfPages =
    hasData && getNumberOfPages(data.searchByCategory!.result!.total, PER_PAGE);
  const hasPagination =
    hasData && numberOfPages && data.searchByCategory!.result!.total > PER_PAGE;

  const activeFilters =
    data.searchByCategory?.result?.activeFilters &&
    data.searchByCategory?.result?.activeFilters.filter(
      (filter) => filter && filter?.field !== 'category'
    );

  if (
    data.searchByCategory &&
    data.searchByCategory.category &&
    activeFilters &&
    activeFilters.length > 0
  ) {
    const suffix = activeFilters.map((filter) => filter.label).join(', ');
    title = `${data.searchByCategory.category.labelPlural} in ${suffix}`;
  }

  const onCloseDrawer = () => setIsDrawerOpen(false);
  const onOpenDrawer = () => setIsDrawerOpen(true);

  const onChangeSorting = (val: string) => {
    if (val && val.indexOf('__') !== -1) {
      const splitted = val.split('__');
      if (splitted.length === 2) {
        const url = autocompleteResultToUrl(
          data.searchByCategory!.category,
          null,
          form.radius ? form.radius + '' : undefined,
          {
            filter: router.query.filter,
            latitude: router.query.latitude,
            longitude: router.query.longitude,
            query: router.query.query,
            radius: form.radius ? form.radius + '' : undefined,
            sort: splitted[0],
            sortOrder: splitted[1],
          }
        );

        if (url) {
          router.push(url);
        }
      }
    }
  };

  const sort = (router.query && (router.query.sort as string)) || 'popularity';

  return (
    <>
      <Layout data={data}>
        {router.isFallback ? (
          <SiteLoading />
        ) : (
          <>
            <FilterDrawer
              open={isDrawerOpen}
              onClose={onCloseDrawer}
              category={data.searchByCategory!.category?.frontendUrl || ''}
              activeFilters={activeFilters || []}
            />
            <SearchForm
              location={form.location}
              query={form.query || ''}
              radius={form.radius || 30}
              categories={data.searchByCategory!.categories || []}
              activeCategory={data.searchByCategory!.category}
              filters={activeFilters || []}
              onOpenDrawer={onOpenDrawer}
            />
            <Flex flex={1} justify="center">
              <Flex flexDirection="column" flex={1}>
                {!isMap && (
                  <Container marginTop={4} marginBottom={8}>
                    <Flex
                      alignItems={{
                        base: 'stretch',
                        md: 'center',
                      }}
                      justifyContent="space-between"
                      flexDirection={{
                        base: 'column',
                        md: 'row',
                      }}
                    >
                      <Box>
                        <Heading as="h1" fontSize="2xl">
                          {title}
                        </Heading>
                      </Box>
                      <Flex>
                        <Flex align={'center'} marginRight={2}>
                          <Box
                            fontSize={'sm'}
                            marginRight={2}
                            whiteSpace="nowrap"
                          >
                            <SortIcon marginRight={1} />
                            Sortiert nach:
                          </Box>
                          <Menu gutter={0} placement="bottom-end">
                            <MenuButton
                              size="sm"
                              variant="unstyled"
                              as={Button}
                              rightIcon={<ChevronDownIcon />}
                            >
                              {RATINGS_MAP[sort]}
                            </MenuButton>
                            <MenuList fontSize="sm">
                              <MenuOptionGroup
                                onChange={onChangeSorting}
                                defaultValue="asc"
                                type="radio"
                              >
                                <MenuItemOption value="popularity__desc">
                                  Beliebtheit
                                </MenuItemOption>
                                {data.searchByCategory!.category.key ===
                                  'hotel' && (
                                  <MenuItemOption value="average_rating__desc">
                                    Beste Bewertung
                                  </MenuItemOption>
                                )}
                                {data.searchByCategory!.category.key ===
                                  'hotel' && (
                                  <MenuItemOption value="rating_count__desc">
                                    Am meisten Bewertungen
                                  </MenuItemOption>
                                )}
                                <MenuItemOption value="title__asc">
                                  Titel
                                </MenuItemOption>
                              </MenuOptionGroup>
                            </MenuList>
                          </Menu>
                        </Flex>
                        <Button
                          width={{
                            base: '100%',
                            md: 'auto',
                          }}
                          marginTop={{
                            base: 4,
                            md: 0,
                          }}
                          onClick={onGoToMap}
                          size="sm"
                          variant="outline"
                        >
                          <MapSearchIcon marginRight={2} />
                          Zur Kartenansicht
                        </Button>
                      </Flex>
                    </Flex>
                  </Container>
                )}
                <Box flex="1">
                  {isMap && (
                    <SearchMap
                      location={form.location}
                      data={
                        (data.searchByCategory!
                          .result as unknown) as HotelActivityPoiConnection
                      }
                      radius={form.radius || 30}
                      onChangeCenter={onChangeCenter}
                    />
                  )}

                  {!isMap ? (
                    hasData ? (
                      <Container paddingBottom={12}>
                        <TeaserGrid>
                          {data.searchByCategory!.result!.items!.map((d) =>
                            renderTeaser(d!)
                          )}
                        </TeaserGrid>

                        {hasPagination && (
                          <Pagination
                            total={data.searchByCategory!.result!.total}
                            perPage={PER_PAGE}
                          />
                        )}
                      </Container>
                    ) : (
                      <Container>
                        <Box marginBottom={4}>
                          Es wurden keine Einträge gefunden. Versuche ein
                          anderen Ort oder einen größeren Radius!
                        </Box>
                      </Container>
                    )
                  ) : null}
                </Box>
              </Flex>
            </Flex>
          </>
        )}
      </Layout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  params,
  res,
  query,
}) => {
  const route = getGraphqlPageRoute(params);

  if (
    process.env.APP_ID! === 'ebikeatlas' ||
    process.env.APP_ID! === 'bikerbetten'
  ) {
    return {
      notFound: true,
    };
  }

  if (route) {
    if (params && params.path.length > 0) {
      let radius = null;
      let location = null;
      let searchQuery = '';

      let offset =
        query && query.page
          ? getOffsetForPage(parseInt(query.page as string, 10), PER_PAGE)
          : 0;
      let limit = 24;

      const isMapView = Boolean(query && query.karte);

      if (isMapView) {
        limit = 500;
        offset = 0;
      }

      const sortField = (query && query.sort) || 'popularity';
      const sortOrder = (query && query.sortOrder) || 'desc';

      const appConfig = getAppConfig();
      const variables:
        | SearchByCategoryQueryQueryVariables
        | SearchByCategoryMapQueryQueryVariables = {
        category: params.path[0],
        frontend: appConfig.config.frontendName,
        sort: {
          field: sortField as string,
          direction: sortOrder === 'desc' ? SortOrder.Desc : SortOrder.Asc,
        },
        filter: {
          conditions: [
            {
              field: 'frontends',
              value: appConfig.config.frontendName,
              operator: '=',
            },
          ],
          scopes: [],
        },
        offset,
        limit,
        menuFooter: appConfig.config.menuFooter,
        menuMain: appConfig.config.menuMain,
      };

      if (query) {
        if (query.filter) {
          if (Array.isArray(query.filter)) {
            query.filter.forEach((filterQuery) => {
              const entityFilter = parseFilterQueryString(filterQuery);

              if (entityFilter) {
                variables.filter!.conditions!.push(entityFilter);
              }
            });
          } else {
            const entityFilter = parseFilterQueryString(query.filter);

            if (entityFilter) {
              variables.filter!.conditions!.push(entityFilter);
            }
          }
        }

        location = {
          lat: query.latitude,
          lng: query.longitude,
        };
        searchQuery = query.query as string;
        radius = query.radius as string;

        if (radius && location) {
          variables.limit = 24;

          variables.filter!.scopes!.push({
            name: 'radius_search',
            parameters: [
              {
                name: 'radius',
                value: parseInt(radius, 10) * 1000 + '',
              },
              {
                name: 'lat',
                value: location.lat + '',
              },
              {
                name: 'lng',
                value: location.lng + '',
              },
            ],
          });
        }
      }

      const data = (await getServerSidePropsData(
        res,
        isMapView ? SearchByCategoryMapQuery : SearchByCategoryQuery,
        variables,
        searchQuery && location && radius
          ? {
              form: {
                query: searchQuery,
                location,
                radius,
              },
            }
          : {}
      )) as any;

      if (!data.props.data.searchByCategory.category) {
        return {
          notFound: true,
        };
      }

      return data;
    }
  }

  return {
    props: {
      data: null,
    },
  };
};

export default SearchDiscover;
